import { z } from "zod";

export const ScheduleDaySchema = z.enum([
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
]);
export type ScheduleDay = z.infer<typeof ScheduleDaySchema>;

const TIME_OF_DAY_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const TimeOfDaySchema = z
  .string()
  .regex(TIME_OF_DAY_REGEX, "Expected HH:MM 24h time");

export const ScheduleWindowSchema = z
  .object({
    days: z.array(ScheduleDaySchema).min(1),
    start: TimeOfDaySchema,
    end: TimeOfDaySchema,
    tz: z.string().optional(),
  })
  .refine(
    (data) => {
      // TODO: To support overnight windows (e.g., 22:00-06:00), remove this
      // validation and update window evaluation logic to handle end < start
      const startParts = data.start.split(":");
      const endParts = data.end.split(":");
      const startMins = Number(startParts[0]) * 60 + Number(startParts[1]);
      const endMins = Number(endParts[0]) * 60 + Number(endParts[1]);
      return endMins > startMins;
    },
    {
      message:
        "end time must be after start time (overnight windows not yet supported)",
      path: ["end"],
    },
  );
export type ScheduleWindow = z.infer<typeof ScheduleWindowSchema>;

export const ScheduleConstraintsSchema = z
  .object({
    min_interval_ms: z.number().int().positive().optional(),
    max_interval_ms: z.number().int().positive().optional(),
    max_runs_per_day: z.number().int().positive().optional(),
    window_ms: z.number().int().positive().optional(),
    jitter_ms: z.number().int().min(0).optional(),
    overlap_policy: z.enum(["skip", "queue", "allow"]).optional(),
    catchup_policy: z.enum(["skip", "catchup"]).optional(),
  })
  .strict();
export type ScheduleConstraints = z.infer<typeof ScheduleConstraintsSchema>;

const BaseScheduleSpecSchema = z.object({
  timezone: z.string().optional(),
  windows: z.array(ScheduleWindowSchema).min(1).optional(),
  constraints: ScheduleConstraintsSchema.optional(),
});

export const IntervalScheduleSpecSchema = BaseScheduleSpecSchema.extend({
  type: z.literal("interval"),
  interval_ms: z.number().int().positive(),
});
export type IntervalScheduleSpec = z.infer<typeof IntervalScheduleSpecSchema>;

export const CronScheduleSpecSchema = BaseScheduleSpecSchema.extend({
  type: z.literal("cron"),
  cron: z.string().min(1),
});
export type CronScheduleSpec = z.infer<typeof CronScheduleSpecSchema>;

export const RRuleScheduleSpecSchema = BaseScheduleSpecSchema.extend({
  type: z.literal("rrule"),
  rrule: z.string().min(1),
});
export type RRuleScheduleSpec = z.infer<typeof RRuleScheduleSpecSchema>;

export const WindowedScheduleSpecSchema = BaseScheduleSpecSchema.extend({
  type: z.literal("windowed"),
  windows: z.array(ScheduleWindowSchema).min(1),
  interval_ms: z.number().int().positive().optional(),
  count_per_window: z.number().int().positive().optional(),
});
export type WindowedScheduleSpec = z.infer<typeof WindowedScheduleSpecSchema>;

/**
 * Base schema for strategy schedule specs - pure types only.
 * For runtime validation of cron/rrule expressions, use
 * StrategyScheduleSpecSchemaWithValidation from @repo/utils/strategy-schedule-validation
 */
export const StrategyScheduleSpecSchema = z.discriminatedUnion("type", [
  IntervalScheduleSpecSchema,
  CronScheduleSpecSchema,
  RRuleScheduleSpecSchema,
  WindowedScheduleSpecSchema,
]);
export type StrategyScheduleSpec = z.infer<typeof StrategyScheduleSpecSchema>;

export type StrategyScheduleKind = StrategyScheduleSpec["type"];
